import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Briefcase, MapPin, IndianRupee, ArrowLeft, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function JobDetailPage() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const showApplyDefault = search.get("apply") === "1";
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(showApplyDefault);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    api
      .get(`/jobs/${id}`)
      .then((r) => setJob(r.data))
      .catch(() => setJob(null))
      .finally(() => setLoading(false));
  }, [id]);

  const onApply = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const form = new FormData(e.currentTarget);
      if (fileRef.current?.files?.[0]) form.set("resume", fileRef.current.files[0]);
      await api.post(`/jobs/${id}/apply`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setSuccess(true);
      toast.success("Application submitted!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Application failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="pt-[120px] min-h-[60vh] text-center text-muted-foreground" data-testid="job-detail-loading">Loading...</main>
        <Footer />
      </>
    );
  }
  if (!job) {
    return (
      <>
        <Navbar />
        <main className="pt-[120px] min-h-[60vh] text-center" data-testid="job-detail-notfound">
          <div className="font-display text-2xl font-bold">Job not found</div>
          <Button onClick={() => navigate("/jobs")} className="mt-4 bg-bee-teal text-white rounded-full">Back to Jobs</Button>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-[72px]" data-testid="job-detail-page">
        <section className="bg-gradient-to-br from-bee-teal to-[hsl(var(--bee-teal-dark))] text-white py-12 lg:py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <button onClick={() => navigate("/jobs")} className="text-sm text-bee-honey hover:text-white inline-flex items-center gap-1.5" data-testid="job-back-link">
              <ArrowLeft className="h-3.5 w-3.5" /> All Jobs
            </button>
            <div className="mt-4 flex items-center gap-2">
              <span className="px-2.5 py-1 rounded-full text-[10px] uppercase font-bold tracking-wider bg-bee-honey text-bee-teal-dark">{job.job_type}</span>
              {job.domain && <span className="text-xs text-white/70">{job.domain}</span>}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-2">{job.title}</h1>
            <div className="mt-5 flex flex-wrap gap-5 text-sm text-white/85">
              <div className="flex items-center gap-1.5"><Briefcase className="h-4 w-4" /> {job.experience}</div>
              <div className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {(job.locations || []).join(", ")}</div>
              {job.ctc && <div className="flex items-center gap-1.5"><IndianRupee className="h-4 w-4" /> {job.ctc}</div>}
            </div>
            {!success && (
              <Button
                onClick={() => setShowApply(true)}
                data-testid="job-apply-cta"
                className="mt-6 bg-bee-honey text-bee-teal-dark hover:bg-white rounded-full px-7 h-12 font-semibold"
              >
                Apply Now
              </Button>
            )}
          </div>
        </section>

        <section className="py-12 lg:py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <h2 className="font-display text-2xl font-bold tracking-tight">Description</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed whitespace-pre-line">{job.description}</p>
              {job.skills?.length > 0 && (
                <>
                  <h3 className="font-display text-lg font-bold mt-8">Key Skills</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {job.skills.map((s) => (
                      <span key={s} className="px-3 py-1 rounded-full text-xs bg-secondary font-medium">{s}</span>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="lg:col-span-1">
              {success ? (
                <div className="rounded-2xl border border-bee-teal/30 bg-bee-teal/5 p-6 text-center" data-testid="apply-success">
                  <CheckCircle2 className="h-10 w-10 text-bee-teal mx-auto" />
                  <div className="mt-3 font-display text-xl font-bold">Application received!</div>
                  <p className="mt-2 text-sm text-muted-foreground">Our team will review and reach out shortly.</p>
                  <Button onClick={() => navigate("/jobs")} className="mt-5 bg-bee-teal text-white rounded-full">View more jobs</Button>
                </div>
              ) : showApply ? (
                <form onSubmit={onApply} className="rounded-2xl border border-border bg-card p-6 sticky top-24" data-testid="apply-form">
                  <h3 className="font-display text-xl font-bold">Apply for this role</h3>
                  <div className="mt-4 space-y-3">
                    <div>
                      <Label htmlFor="afull_name">Full Name</Label>
                      <Input id="afull_name" name="full_name" required data-testid="apply-name" />
                    </div>
                    <div>
                      <Label htmlFor="aemail">Email</Label>
                      <Input id="aemail" name="email" type="email" required data-testid="apply-email" />
                    </div>
                    <div>
                      <Label htmlFor="aphone">Phone</Label>
                      <Input id="aphone" name="phone" required data-testid="apply-phone" />
                    </div>
                    <div>
                      <Label htmlFor="acover">Cover Note (optional)</Label>
                      <Textarea id="acover" name="cover_message" rows={3} data-testid="apply-cover" />
                    </div>
                    <div>
                      <Label htmlFor="aresume">Resume (PDF/DOC, max 5MB)</Label>
                      <label
                        htmlFor="aresume"
                        className="mt-1 flex items-center justify-center gap-2 w-full border-2 border-dashed border-border rounded-lg px-4 py-5 cursor-pointer hover:border-bee-teal hover:bg-secondary/40 transition-colors"
                      >
                        <Upload className="h-4 w-4 text-bee-teal" />
                        <span className="text-sm">{fileName || "Click to upload"}</span>
                      </label>
                      <input
                        id="aresume"
                        ref={fileRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        required
                        className="hidden"
                        onChange={(e) => setFileName(e.target.files?.[0]?.name || "")}
                      />
                    </div>
                    <Button type="submit" disabled={submitting} data-testid="apply-submit" className="w-full h-11 bg-bee-teal text-white hover:bg-[hsl(var(--bee-teal-dark))] rounded-full">
                      {submitting ? "Submitting..." : "Submit Application"}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="rounded-2xl border border-border bg-card p-6 sticky top-24">
                  <div className="font-display text-lg font-bold">Ready to apply?</div>
                  <p className="mt-2 text-sm text-muted-foreground">Send us your resume and we&apos;ll fast-track your profile.</p>
                  <Button onClick={() => setShowApply(true)} className="mt-4 w-full bg-bee-teal text-white rounded-full">Apply Now</Button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
